import { DataGrid, GridColumnProps, SelectFilterComponent, SelectFloatingFilterComponent, SelectSearchFloatingFilterComponent } from '@roc/ui';
import { observer } from 'mobx-react';
import { Box, Grid, makeStyles } from '@material-ui/core';
import { useEffect } from 'react';
import { usePrincipalAmortizationStore } from '../../hooks/usePrincipalAmortizationStore';
import { DateFormat, formatDate } from '@roc/feature-utils';
import { dateFilterProps, filterProps, formatCurrency, noOpComparator } from '@roc/ui/utils';
import { PrincipalAmortizationToolbar } from './principalAmortizationToolbar';

const useStyles = makeStyles(theme => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '65vh',
    overflow: 'hidden',
  },
}));

export const columns: GridColumnProps[] = [
  {
    field: 'loanId',
    headerName: 'Loan Id',
    minWidth: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'duration',
    headerName: 'Duration',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'dueDate',
    headerName: 'Due Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'startingPrincipal',
    headerName: 'Starting Principal',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'endingBalance',
    headerName: 'Ending Balance',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'interest',
    headerName: 'Interest',
    minWidth: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'comments',
    headerName: 'Comments',
    minWidth: 120,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'monthlyInterest',
    headerName: 'Monthly Interest',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'principal',
    headerName: 'Principal',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'pmtFunc',
    headerName: 'PMT Func',
    minWidth: 90,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
];

export const PrincipalAmortizationGrid = observer(() => {
  const classes = useStyles();
  const { principalAmortizationStore } = usePrincipalAmortizationStore();
  const { principalAmortizationGridStore, tab, loan } = principalAmortizationStore;

  useEffect(() => {
    principalAmortizationGridStore.resetAndFetchGridData();
  }, [loan]);

  const frameworkComponents = {
    selectFloatingFilter: SelectFloatingFilterComponent,
    selectSearchFloatingFilter: SelectSearchFloatingFilterComponent,
    selectFilter: SelectFilterComponent,
    dateCellRenderer: ({ value }) =>
      formatDate(value, DateFormat.MMDDYYYY) ?? null,
    currencyCellRenderer({ value }) {
      return value ? formatCurrency(value) : '-';
    },
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    principalAmortizationGridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    principalAmortizationGridStore.setFilterModel(filterModel);
  };

  return (
    <Box>
      <Grid item className={classes.gridContainer}>
        <DataGrid
          columns={columns}
          rows={principalAmortizationGridStore.gridData.data.rows}
          frameworkComponents={frameworkComponents}
          headerCheckboxSelection={true}
          suppressRowClickSelection={true}
          key={tab}
          toolbar={<PrincipalAmortizationToolbar />}
          onSortChanged={onSortChanged}
          onFilterChanged={onFilterChanged}
          isLoading={principalAmortizationGridStore.isLoading}
          showFilters={principalAmortizationGridStore.showFilters}
          sortModel={principalAmortizationGridStore.sortModel}
          filterModel={principalAmortizationGridStore.filterModel}
          paginationData={principalAmortizationGridStore.gridData.meta}
          setPageNumber={principalAmortizationGridStore.setPageNumber}
        />
      </Grid>
    </Box>
  );
});
